const PRICE_PADDING_RATIO = 0.04;
const MAX_VISIBLE_REGIME_ZONES = 2;
const LOW_TIMEFRAME_INTERVALS = new Set(["1", "3", "5", "15"]);

const STATE_STYLES = {
  BULL: {
    backgroundColor: "rgba(73,232,132,0.10)",
    bordercolor: "rgba(73,232,132,0.24)"
  },
  BEAR: {
    backgroundColor: "rgba(255,95,95,0.10)",
    bordercolor: "rgba(255,95,95,0.24)"
  },
  NEUTRAL: {
    backgroundColor: "rgba(255,200,87,0.08)",
    bordercolor: "rgba(255,200,87,0.20)"
  }
};

const numeric = (value) => {
  const number = Number(value);
  return Number.isFinite(number) && number > 0 ? number : null;
};

const chartPrice = (value) => Number(Number(value).toPrecision(12));

const timeFromBar = (bar) => {
  if (!bar) return null;
  const value = bar.value;
  const candidates = [
    bar.time,
    bar.timestamp,
    value?.time,
    value?.timestamp,
    Array.isArray(value) ? value[0] : null
  ];
  for (const candidate of candidates) {
    const time = Number(candidate);
    if (Number.isFinite(time) && time > 0) return time;
  }
  return null;
};

const isBarInVisibleRange = (bar, visibleRange) => {
  if (!visibleRange) return true;
  const from = Number(visibleRange.from);
  const to = Number(visibleRange.to);
  if (!Number.isFinite(from) || !Number.isFinite(to) || to <= from) return true;
  const time = timeFromBar(bar);
  return time == null || (time >= from && time <= to);
};

const priceValuesFromBar = (bar) => {
  if (!bar) return [];
  const values = [];
  const value = bar.value;

  if (Array.isArray(value)) {
    const ohlc = value.length >= 5 ? value.slice(1, 5) : value;
    ohlc.forEach((item) => {
      const price = numeric(item);
      if (price != null) values.push(price);
    });
  }

  [
    bar.high,
    bar.low,
    bar.open,
    bar.close,
    value?.high,
    value?.low,
    value?.open,
    value?.close
  ].forEach((item) => {
    const price = numeric(item);
    if (price != null) values.push(price);
  });

  return values;
};

export const priceBoundsFromTradingViewBars = (bars, visibleRange = null) => {
  const items = Array.isArray(bars) ? bars : bars?._items;
  if (!items || items.length === 0) return null;

  let low = Number.POSITIVE_INFINITY;
  let high = Number.NEGATIVE_INFINITY;

  items.filter((bar) => isBarInVisibleRange(bar, visibleRange)).forEach((bar) => {
    priceValuesFromBar(bar).forEach((price) => {
      low = Math.min(low, price);
      high = Math.max(high, price);
    });
  });

  if (!Number.isFinite(low) || !Number.isFinite(high) || high <= low) {
    return null;
  }

  return { low, high };
};

export const priceBoundsFromChart = (chart) => {
  try {
    const bars = chart?.getSeries?.()?.data?.()?.bars?.();
    const visibleRange = typeof chart?.getVisibleRange === "function" ? chart.getVisibleRange() : null;
    return priceBoundsFromTradingViewBars(bars, visibleRange) || priceBoundsFromTradingViewBars(bars);
  } catch {
    return null;
  }
};

const btcRegimeZonesForInterval = (zones, interval) => {
  const normalized = String(interval || "").toUpperCase();
  return LOW_TIMEFRAME_INTERVALS.has(normalized)
    ? zones.slice(-MAX_VISIBLE_REGIME_ZONES)
    : zones;
};

export const buildBtcRegimeDrawings = (btcRegime, priceBounds, interval = null) => {
  if (!btcRegime?.zones?.length || !priceBounds) return [];
  const low = Number(priceBounds.low);
  const high = Number(priceBounds.high);
  if (!Number.isFinite(low) || !Number.isFinite(high) || high <= low) return [];

  const padding = (high - low) * PRICE_PADDING_RATIO;
  const paddedLow = chartPrice(low - padding);
  const paddedHigh = chartPrice(high + padding);

  return btcRegimeZonesForInterval(btcRegime.zones, interval)
    .map((zone) => {
      const state = String(zone?.state || "NEUTRAL").toUpperCase();
      const fromTime = Number(zone?.fromTime);
      const toTime = Number(zone?.toTime);
      if (!Number.isFinite(fromTime) || !Number.isFinite(toTime) || toTime <= fromTime) {
        return null;
      }
      const style = STATE_STYLES[state] || STATE_STYLES.NEUTRAL;
      const text = `BTC ${state}`;
      return {
        point: [
          { time: fromTime, price: paddedLow },
          { time: toTime, price: paddedHigh }
        ],
        options: {
          shape: "rectangle",
          text,
          disableSelection: true,
          disableSave: true,
          lock: true,
          zOrder: "bottom",
          overrides: {
            backgroundColor: style.backgroundColor,
            bordercolor: style.bordercolor,
            linewidth: 1,
            linestyle: 0,
            text,
            color: style.bordercolor
          }
        }
      };
    })
    .filter(Boolean);
};
